import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { submitAiContestEnrollment, uploadAiContestAttachment } from "../repo";
import { createEmptyEnrollmentDraft, validateEnrollmentDraft } from "../service";
import type {
  AiContestAttachment,
  AiContestEnrollmentDraft,
  AiContestEnrollmentResult,
} from "../types";

/** 报名页运行时状态与动作。 */
export function useAiContestEnrollmentRuntime() {
  const [draft, setDraft] = useState<AiContestEnrollmentDraft>(createEmptyEnrollmentDraft);
  const [attachments, setAttachments] = useState<AiContestAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [result, setResult] = useState<AiContestEnrollmentResult | null>(null);
  const [errorScrollToken, setErrorScrollToken] = useState(0);

  const bumpErrorScroll = useCallback(() => {
    setErrorScrollToken((current) => current + 1);
  }, []);

  const submitMutation = useMutation({
    mutationFn: () => submitAiContestEnrollment(draft, attachments),
    onSuccess(data) {
      setResult(data);
      setLocalError(null);
    },
    onError() {
      bumpErrorScroll();
    },
  });

  const updateDraft = useCallback((patch: Partial<AiContestEnrollmentDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setUploading(true);
      setLocalError(null);
      try {
        const uploaded: AiContestAttachment[] = [];
        for (const file of list) {
          uploaded.push(await uploadAiContestAttachment(file));
        }
        setAttachments((current) => [...current, ...uploaded]);
      } catch (error) {
        setLocalError(error instanceof Error ? error.message : "附件上传失败");
        bumpErrorScroll();
      } finally {
        setUploading(false);
      }
    },
    [bumpErrorScroll],
  );

  const removeAttachment = useCallback((mediaKey: string) => {
    setAttachments((current) => current.filter((item) => item.mediaKey !== mediaKey));
  }, []);

  const submit = useCallback(() => {
    const validationError = validateEnrollmentDraft(draft);
    if (validationError) {
      setLocalError(validationError);
      bumpErrorScroll();
      return;
    }
    setLocalError(null);
    submitMutation.mutate();
  }, [bumpErrorScroll, draft, submitMutation]);

  return {
    draft,
    attachments,
    uploading,
    submitting: submitMutation.isPending,
    localError,
    submitError: submitMutation.error instanceof Error ? submitMutation.error.message : null,
    errorScrollToken,
    result,
    updateDraft,
    addFiles,
    removeAttachment,
    submit,
  };
}
